import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { ComponentProps } from "react";
import { useSearch } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import { Check, MapPinOff, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { ProjectDetail, ProjectRecord } from "@/types/api";
import { coverImage } from "@/lib/record-images";
import { PhotoGrid } from "@/components/records/photo-grid/photo-grid";
import { PhotoList } from "@/components/records/photo-list/photo-list";
import { RecordsViewToggle } from "@/components/records/records-view-toggle/records-view-toggle";
import { useRecordsViewMode } from "@/components/records/records-view/records-view-mode";
import { RecordsSidebar } from "@/components/common/records-sidebar/records-sidebar";
import { readStoredWidth } from "@/components/common/records-sidebar/use-sidebar-width";
import { useElementWidth } from "@/hooks/use-element-width";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import { RecordsToolbar } from "@/components/common/records-toolbar/records-toolbar";
import { PageTitleRow } from "@/components/common/page-title-row/page-title-row";
import { useRecordCreators } from "@/components/common/creator-filter/use-record-creators";
import { RecordLimitBanner } from "@/components/records/record-limit-banner/record-limit-banner";
import { ImageViewerModal } from "@/components/common/image-viewer-modal/image-viewer-modal";
import { ReportGenerationDialog } from "@/components/records/report-generation-dialog/report-generation-dialog";
import { useSelectionMode } from "@/components/records/selection-mode/selection-mode-provider";
import { MAX_ISSUE_BATCH } from "@/components/records/selection-mode/bulk-action-bar";
import {
  RaiseIssueSheet,
  type BatchRaiseRecord,
} from "@/components/issues/raise-issue-sheet/raise-issue-sheet";
import { readMetadataByFieldName } from "@/components/common/image-viewer-modal/use-submit-record";
import { useBulkDeleteRecords } from "@/hooks/use-record-mutations";
import { useModalNavigation } from "@/hooks/use-modal-navigation";
import { useProjectRecords } from "@/components/records/photo-grid/use-project-records";
import { ApiError, OfflineError } from "@/lib/api";
import { compressImage } from "@/lib/compress-image";
import {
  usePermissions,
  useCanOnResource,
} from "@/hooks/use-permissions";
import { useOnlineStatus } from "@/offline/online-status";
import { useRecordsFilterState } from "./use-records-filter-state";
import { resolveOpenRecord } from "./resolve-open-record";
import {
  resolveSidePanelLayout,
  sidePanelWidth,
} from "@/lib/side-panel-layout";
import { setRecordPanelInset } from "./record-panel-inset";

interface RecordsViewProps {
  projectId: string;
  project: ProjectDetail;
}

/** Stable extractors + empty fallback so useModalNavigation's index memo and
 * effects don't re-run on every parent render. */
const getRecordId = (r: ProjectRecord) => r.id;
// Resolve the cover through the [[record-images]] seam so the modal opens on
// the right photo once the v2.0 `images[]` read ships (today it's `image`).
const getRecordImageUrl = (r: ProjectRecord) => coverImage(r)?.image ?? null;
const NO_RECORDS: ProjectRecord[] = [];

export function RecordsView({ projectId, project }: RecordsViewProps) {
  // URL params seed filter state on mount — see records-view.spec.md §
  // URL-seeded filter state. Entry point: unpinned-photos-widget
  // (`?has_pin=false`). `strict: false` because some tests render this view
  // outside the route; they mock `useSearch` to return `{}`. The route's
  // `validateSearch` (records/index.tsx) is the source of truth for the
  // accepted param shape — keep that in sync with the cast below.
  const initialSearch = useSearch({ strict: false }) as {
    has_pin?: boolean;
    plan?: string;
  };
  // useState ignores subsequent values, so the URL is read exactly once.
  const [initial] = useState({
    hasPin: initialSearch.has_pin === false ? false : null,
    selectedPlanId: initialSearch.plan ?? null,
  });
  const state = useRecordsFilterState(initial);
  const isOnline = useOnlineStatus();
  const creators = useRecordCreators(projectId).data ?? [];
  const [selectedRecord, setSelectedRecord] = useState<ProjectRecord | null>(
    null,
  );

  // Desktop frames an open record as a panel beside the gallery rather than a
  // dialog over it, when there is room. See [[records-view.spec]] US-006.
  const isDesktop = useIsDesktop();
  const rootRef = useRef<HTMLDivElement>(null);
  const availableWidth = useElementWidth(rootRef);
  const [sidebarWidth, setSidebarWidth] = useState(readStoredWidth);
  const layout = resolveSidePanelLayout({
    isOpen: selectedRecord !== null,
    isDesktop,
    availableWidth,
    sidebarWidth,
  });
  const isSplit = layout === "split" || layout === "split-no-sidebar";

  // Publish the panel's width so the bottom nav's Upload button can step left
  // of it instead of covering the panel's action bar.
  const panelRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!isSplit || !el) {
      setRecordPanelInset(0);
      return;
    }
    setRecordPanelInset(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() =>
      setRecordPanelInset(el.getBoundingClientRect().width),
    );
    ro.observe(el);
    return () => {
      ro.disconnect();
      setRecordPanelInset(0);
    };
  }, [isSplit]);
  const showSidebar = layout !== "split-no-sidebar";

  // The gallery beside the panel can swap the open record while it is being
  // edited; ask before that discards the edit.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingRecord, setPendingRecord] = useState<ProjectRecord | null>(
    null,
  );
  const handleRecordClick = useCallback(
    (record: ProjectRecord) => {
      if (
        isSplit &&
        hasUnsavedChanges &&
        selectedRecord &&
        record.id !== selectedRecord.id
      ) {
        setPendingRecord(record);
        return;
      }
      setSelectedRecord(record);
    },
    [isSplit, hasUnsavedChanges, selectedRecord],
  );

  const permissionsProject = {
    owner: project.owner.id,
    my_roles: project.my_roles,
  };
  const { can } = usePermissions(permissionsProject);
  const [viewMode] = useRecordsViewMode();
  const canOnResourceFn = useCanOnResource(permissionsProject);

  // Selection-mode coordination — see [[selection-mode.spec]] § Provider
  // architecture. The provider holds counters + flags; this view owns the
  // actual selection arrays.
  const {
    isSelectionMode,
    isReportMode,
    enterSelectionMode,
    exitSelectionMode,
    exitReportMode,
    setSelectedCount,
    setIsAllSelected,
    setIsDeletingRecords,
    registerDeleteAction,
    registerReportAction,
    registerIssuesAction,
    registerShareAction,
    registerSelectAllAction,
    registerUnselectAllAction,
  } = useSelectionMode();

  // Per [[selection-mode.spec]] § Provider architecture, we keep both
  // selectedIds (used for the bulk delete payload + grid item indicator
  // membership lookups) and selectedRecords (used by the report dialog,
  // which needs the full ProjectRecord objects). They're kept in sync by
  // toggleSelection.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedRecords, setSelectedRecords] = useState<ProjectRecord[]>([]);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);

  // Selecting photos and reading one record are separate tasks; entering
  // selection mode closes the open record (only reachable with the panel —
  // the dialog covers the Select button).
  // Adjusted during render rather than in an effect, so the panel never paints
  // a frame alongside the selection UI.
  const [prevSelectionMode, setPrevSelectionMode] = useState(isSelectionMode);
  if (prevSelectionMode !== isSelectionMode) {
    setPrevSelectionMode(isSelectionMode);
    if (isSelectionMode) setSelectedRecord(null);
  }
  const [loadedRecords, setLoadedRecords] = useState<ProjectRecord[]>([]);

  // Dialog state lifted out of [[report-generation-dialog.spec]] so it
  // survives close-and-reopen via "Add more photos". Reset by the effect
  // below when selection mode exits (which covers Generate success, X /
  // backdrop / Escape / footer Cancel, and the report-mode Cancel button
  // in the bulk action bar — all routes eventually flip isSelectionMode
  // to false). See [[report-generation-dialog.spec]] § State ownership.
  const [reportName, setReportName] = useState("");
  const [reportRemarks, setReportRemarks] = useState<Record<string, string>>({});
  // Defaults to ON: generating a report writes the edited remarks back to the
  // source records unless the user opts out. See
  // [[report-generation-dialog.spec]] § "Save remarks to records" toggle.
  const [reportOverrideRemarks, setReportOverrideRemarks] = useState(true);

  const handleItemsChange = useCallback((records: ProjectRecord[]) => {
    setLoadedRecords(records);
  }, []);

  // Tell the provider when every loaded photo is selected, so the FAB flips
  // to "Unselect All". Per spec § Edge Cases: "Select All when not all pages
  // are loaded — selects all currently loaded items only".
  const isAllLoadedSelected =
    selectedIds.length > 0 && selectedIds.length === loadedRecords.length;
  useEffect(() => {
    setIsAllSelected(isAllLoadedSelected);
  }, [isAllLoadedSelected, setIsAllSelected]);

  useEffect(() => {
    setSelectedCount(selectedIds.length);
  }, [selectedIds, setSelectedCount]);

  // Whenever the user exits selection mode (header Cancel, navigation,
  // optimistic-delete success), drop selections so the next entry starts
  // clean. Per [[selection-mode.spec]] US-006 § "all selections are cleared".
  // Same external-state-mirror exception as above. Also clears the lifted
  // report-dialog state so the next report flow starts fresh — see
  // [[report-generation-dialog.spec]] § State ownership and reset rules.
  useEffect(() => {
    if (!isSelectionMode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedIds([]);
      setSelectedRecords([]);
      setReportName("");
      setReportRemarks({});
      setReportOverrideRemarks(true);
    }
  }, [isSelectionMode]);

  const handleToggleSelection = useCallback((record: ProjectRecord) => {
    setSelectedIds((prev) =>
      prev.includes(record.id)
        ? prev.filter((id) => id !== record.id)
        : [...prev, record.id],
    );
    setSelectedRecords((prev) =>
      prev.some((r) => r.id === record.id)
        ? prev.filter((r) => r.id !== record.id)
        : [...prev, record],
    );
  }, []);

  const handleLongPressRecord = useCallback(
    (record: ProjectRecord) => {
      if (!isSelectionMode) {
        enterSelectionMode();
      }
      handleToggleSelection(record);
    },
    [isSelectionMode, enterSelectionMode, handleToggleSelection],
  );

  // Bulk delete — optimistic exception per [[mutations.spec]]. The hook
  // does the cache snapshot/patch/rollback; we own the UX (exit selection
  // mode immediately, toast on success, error toast on rejection).
  const bulkDelete = useBulkDeleteRecords();
  // Sync the mutation hook's pending flag to the provider so the bulk
  // action bar's Delete button shows its spinner. The setter is a context
  // function (not a local useState setter), so the set-state-in-effect
  // rule doesn't flag it.
  useEffect(() => {
    setIsDeletingRecords(bulkDelete.isPending);
  }, [bulkDelete.isPending, setIsDeletingRecords]);

  const handleBulkDelete = useCallback(() => {
    if (selectedIds.length === 0) return;
    const count = selectedIds.length;
    const ids = [...selectedIds];

    // Exit selection mode right away — the optimistic patch already removed
    // the tiles from the grid, so leaving the bar up would feel laggy.
    exitSelectionMode();

    bulkDelete.mutate(
      { projectId, recordIds: ids },
      {
        onSuccess: () => {
          toast(`${count} image${count === 1 ? "" : "s"} deleted`);
        },
        onError: (error) => {
          // Discriminated copy — never leak the raw `error.message` (Safari
          // surfaces internal jargon like "Load failed"). The snapshot
          // rollback already happened in `onError` of the mutation hook.
          if (error instanceof OfflineError) {
            toast("Offline — reconnect to delete these records");
          } else if (error instanceof ApiError) {
            toast("Couldn't delete these records — please try again");
          } else {
            toast("Couldn't delete — check your connection and try again");
          }
          // Wrapped as `captureMessage` to bypass `instrument.ts`'s global
          // `ignoreErrors` filter — see use-delete-record / dialog comment
          // for the same wrap pattern and `specs/conventions/error-reporting.md`.
          // The guard below skips `OfflineError` explicitly: `beforeSend` only
          // filters exception events (it inspects `event.exception.values`),
          // not message events from `captureMessage` — so we cannot rely on
          // it as a backstop here.
          if (!(error instanceof OfflineError)) {
            Sentry.captureMessage("Bulk delete records request failed", {
              level: "error",
              tags: { area: "record-bulk-delete" },
              extra: {
                projectId,
                recordCount: count,
                errorName: error instanceof Error ? error.name : "Unknown",
                errorMessage:
                  error instanceof Error ? error.message : String(error),
                status: error instanceof ApiError ? error.status : undefined,
              },
            });
          }
        },
      },
    );
  }, [bulkDelete, exitSelectionMode, projectId, selectedIds]);

  // Batch raise — see [[selection-mode.spec]] US-007. The sheet is handed a
  // snapshot of the selection when it opens and owns it from then on, so
  // selection mode exiting mid-batch (connection drop, navigation) can neither
  // cut the batch short nor hide its result.
  const [issueBatch, setIssueBatch] = useState<{
    records: BatchRaiseRecord[];
    skippedCount: number;
  } | null>(null);

  const handleRaiseIssues = useCallback(() => {
    if (
      selectedRecords.length === 0 ||
      selectedRecords.length > MAX_ISSUE_BATCH
    ) {
      return;
    }
    // A photo still uploading has no server id to hang an issue on. The
    // loaded list is server records only, so membership in it is the test.
    const serverIds = new Set(loadedRecords.map((r) => r.id));
    const raisable = selectedRecords.filter((r) => serverIds.has(r.id));
    if (raisable.length === 0) {
      toast("These photos are still uploading — raise issues once they sync");
      return;
    }
    setIssueBatch({
      records: raisable.map((r) => ({
        id: r.id,
        remarks: readMetadataByFieldName(
          project.metadata_fields ?? [],
          r.metadata,
          "remarks",
        ),
      })),
      skippedCount: selectedRecords.length - raisable.length,
    });
  }, [selectedRecords, loadedRecords, project.metadata_fields]);

  // Raised records leave the selection as soon as they are reported, partial
  // batch or not — a second tap of Issues must never raise them twice.
  const handleBatchRaised = useCallback((recordIds: string[]) => {
    const raised = new Set(recordIds);
    setSelectedIds((prev) => prev.filter((id) => !raised.has(id)));
    setSelectedRecords((prev) => prev.filter((r) => !raised.has(r.id)));
  }, []);

  const handleBatchComplete = useCallback(
    (raisedCount: number) => {
      toast(`${raisedCount} issue${raisedCount === 1 ? "" : "s"} raised`, {
        icon: <Check className="h-4 w-4" />,
      });
      exitSelectionMode();
    },
    [exitSelectionMode],
  );

  const handleSelectAll = useCallback(() => {
    setSelectedIds(loadedRecords.map((r) => r.id));
    setSelectedRecords(loadedRecords);
  }, [loadedRecords]);

  const handleUnselectAll = useCallback(() => {
    setSelectedIds([]);
    setSelectedRecords([]);
  }, []);

  // Bulk share — fetch each selected record's full image, compress, then
  // hand the file array to navigator.share. AbortError = user dismissed,
  // not a failure. Other failures surface via toast (no Sentry — these are
  // expected user-facing flows per [[error-reporting.spec]]).
  const handleBulkShare = useCallback(async () => {
    if (selectedRecords.length === 0) return;

    const imageUrls = selectedRecords
      .map((r) => coverImage(r)?.image)
      .filter((url): url is string => !!url);
    const fetchResults = await Promise.allSettled(
      imageUrls.map((url) => fetch(url, { credentials: "omit" })),
    );

    const compressPromises: Promise<Blob>[] = [];
    const filenames: string[] = [];
    for (let i = 0; i < fetchResults.length; i++) {
      const result = fetchResults[i];
      if (result.status !== "fulfilled" || !result.value.ok) continue;
      const filename =
        imageUrls[i].split("/").pop()?.split("?")[0] || `photo-${i}.jpg`;
      filenames.push(filename);
      compressPromises.push(
        result.value.blob().then((blob) => compressImage(blob, filename)),
      );
    }

    const compressResults = await Promise.allSettled(compressPromises);
    const files: File[] = [];
    compressResults.forEach((r, i) => {
      if (r.status !== "fulfilled") return;
      // compressImage returns a Blob; native share wants a File so the
      // device can suggest filenames in mail / messaging clients.
      files.push(
        new File([r.value], filenames[i], { type: r.value.type }),
      );
    });

    if (files.length === 0) {
      toast("No photos available to share");
      return;
    }

    if (!navigator.share) {
      toast("Sharing isn't available on this device");
      return;
    }

    try {
      await navigator.share({ files });
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        // User dismissed the sheet — not a failure.
        return;
      }
      toast("Couldn't open the share sheet");
    }
  }, [selectedRecords]);

  // Register all six action callbacks with the provider. Refs in the
  // provider keep these stable across renders, so we just publish on every
  // change. (Re-registering on every render is cheap because the provider
  // doesn't trigger re-renders for ref writes.)
  useEffect(() => {
    registerDeleteAction(handleBulkDelete);
  }, [handleBulkDelete, registerDeleteAction]);
  useEffect(() => {
    registerReportAction(() => setIsReportDialogOpen(true));
  }, [registerReportAction]);
  useEffect(() => {
    registerIssuesAction(handleRaiseIssues);
  }, [handleRaiseIssues, registerIssuesAction]);
  useEffect(() => {
    registerShareAction(handleBulkShare);
  }, [handleBulkShare, registerShareAction]);
  useEffect(() => {
    registerSelectAllAction(handleSelectAll);
  }, [handleSelectAll, registerSelectAllAction]);
  useEffect(() => {
    registerUnselectAllAction(handleUnselectAll);
  }, [handleUnselectAll, registerUnselectAllAction]);

  // "Full exit" close path: X, backdrop, Escape, footer Cancel button. In a
  // report-mode session (started from New Report) the dialog closes AND the
  // session ends back on the Reports page, where it was started; the effect
  // above then clears selections + lifted report state. Outside report mode
  // the dialog just closes and the selection stays.
  // Spec: [[selection-mode.spec]] § Report mode, [[report-generation-dialog.spec]].
  const handleReportDialogOpenChange = useCallback(
    (open: boolean) => {
      setIsReportDialogOpen(open);
      if (!open && isReportMode) exitReportMode();
    },
    [exitReportMode, isReportMode],
  );

  // "Add more photos" close path: dialog closes visually but URL and
  // selection mode are preserved so the user can pick more photos and
  // reopen with everything they typed intact. Spec:
  // [[report-generation-dialog.spec]] US-005.
  const handleRequestAddMore = useCallback(() => {
    setIsReportDialogOpen(false);
  }, []);

  // Per-photo X-button removal in the report dialog. The dialog is
  // stateless on selection — it reports the removal here so the parent's
  // selectedIds + selectedRecords stay aligned. When this drains the set
  // to zero, the dialog auto-closes (see report-generation-dialog.spec
  // US-004).
  const handleRemoveReportRecord = useCallback((recordId: string) => {
    setSelectedIds((prev) => prev.filter((id) => id !== recordId));
    setSelectedRecords((prev) => prev.filter((r) => r.id !== recordId));
  }, []);

  // Prev/next navigation across the loaded grid records inside the image
  // viewer — see [[image-viewer-modal.spec]] US-004. We subscribe to the same
  // records query PhotoGrid uses (TanStack dedupes by key, so no second
  // fetch), which gives us the live list plus pagination — letting navigation
  // fetch the next page and continue past the last loaded photo.
  const recordsQuery = useProjectRecords(projectId, state.filters);
  const navItems = recordsQuery.data?.records ?? NO_RECORDS;
  // The record handed to the open viewer is resolved LIVE from the records query
  // (the same list nav reads), not the frozen `selectedRecord` snapshot captured
  // at tile-tap. So when a background append lands while the viewer is open, the
  // record's `images[]` gains the synced photo and the viewer swaps the optimistic
  // tile for it in place — no vanish. See [[append-photo-outbox]] and
  // `resolve-open-record`.
  const openRecord = resolveOpenRecord(selectedRecord, navItems);
  const {
    onPrev: onNavigatePrev,
    onNext: onNavigateNext,
    currentIndex: navCurrentIndex,
    totalCount: navTotalCount,
    isFetchingNextPage: navIsFetchingNextPage,
  } = useModalNavigation({
    items: navItems,
    selectedId: selectedRecord?.id ?? null,
    getId: getRecordId,
    getImageUrl: getRecordImageUrl,
    onNavigate: setSelectedRecord,
    hasNextPage: recordsQuery.hasNextPage,
    fetchNextPage: recordsQuery.fetchNextPage,
    isFetchingNextPage: recordsQuery.isFetchingNextPage,
  });

  const viewerProps: ComponentProps<typeof ImageViewerModal> = {
    isOpen: selectedRecord !== null,
    onClose: () => setSelectedRecord(null),
    imageSrc: openRecord ? (coverImage(openRecord)?.image ?? null) : null,
    projectId,
    metadataFields: project.metadata_fields,
    projectTags: project.tags,
    projectPlans: project.plans,
    initialRecord: openRecord,
    onNavigatePrev: onNavigatePrev ?? undefined,
    onNavigateNext: onNavigateNext ?? undefined,
    currentIndex: navCurrentIndex,
    totalCount: navTotalCount,
    isFetchingNextPage: navIsFetchingNextPage,
    canOnResource: (domain, verb, createdBy) =>
      canOnResourceFn(domain, verb, createdBy),
    canCreate: can("record:Create"),
    canCreateTag: can("tag:Create"),
    canCreateIssue: can("issue:Create"),
    // Reading issues needs no verb, so this is not gated on
    // issue:Create — a View-role member still sees them.
    showRecordIssues: true,
    onUnsavedChangesChange: setHasUnsavedChanges,
  };

  return (
    <div ref={rootRef} className="flex h-full min-w-0 flex-1">
      {showSidebar && (
      <RecordsSidebar
        searchQuery={state.searchQuery}
        onSearchChange={isOnline ? state.setSearchQuery : undefined}
        tags={project.tags ?? []}
        selectedTags={state.selectedTags}
        onTagToggle={state.toggleTag}
        showUntagged={state.showUntagged}
        onUntaggedToggle={state.toggleUntagged}
        plans={project.plans}
        selectedPlanIds={state.selectedPlanIds}
        onPlanToggle={state.togglePlan}
        dateFrom={state.dateFrom}
        dateTo={state.dateTo}
        onDateFromChange={state.setDateFrom}
        onDateToChange={state.setDateTo}
        creators={creators}
        selectedCreatorIds={state.selectedCreatorIds}
        onCreatorToggle={state.toggleCreator}
        onWidthChange={setSidebarWidth}
      />
      )}
      {/* The record panel has a fixed width per breakpoint; the gallery takes
          the rest. With no record open the gallery takes all of it. */}
      <div className="flex min-w-0 flex-1">
      <div className="flex min-w-0 flex-1 flex-col">
        {/* The page title and the grid/list toggle, in the gallery column so an
            open record panel stays beside it, not under it. The toolbar below
            is the search row where the sidebar isn't showing. See
            [[page-title-row.spec]] and [[records-view-toggle.spec]]. */}
        <PageTitleRow title="Photos">
          <RecordsViewToggle />
        </PageTitleRow>
        <RecordsToolbar
          tags={project.tags ?? []}
          plans={project.plans}
          searchQuery={state.searchQuery}
          onSearchChange={isOnline ? state.setSearchQuery : undefined}
          selectedTags={state.selectedTags}
          onTagToggle={state.toggleTag}
          showUntagged={state.showUntagged}
          onUntaggedToggle={state.toggleUntagged}
          selectedPlanIds={state.selectedPlanIds}
          onPlanToggle={state.togglePlan}
          dateFrom={state.dateFrom}
          dateTo={state.dateTo}
          onDateFromChange={state.setDateFrom}
          onDateToChange={state.setDateTo}
          creators={creators}
          selectedCreatorIds={state.selectedCreatorIds}
          onCreatorToggle={state.toggleCreator}
          hasPin={state.hasPin}
          onClearHasPin={state.clearHasPin}
          onClearAll={state.clearAll}
          // With the sidebar stepped aside the toolbar carries search and the
          // filter sheet on desktop too.
          alwaysVisible={!showSidebar}
        />
        <RecordLimitBanner
          recordsCount={project.records_count}
          maxRecordCount={project.max_record_count}
        />
        {/* Desktop-only inline chip — mobile uses RecordsToolbar's strip. */}
        {state.hasPin === false && showSidebar && (
          <div className="hidden md:flex px-4 pt-2">
            <Badge
              variant="secondary"
              className="gap-1 cursor-pointer"
              role="button"
              tabIndex={0}
              aria-label="Remove unpinned filter"
              onClick={state.clearHasPin}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  state.clearHasPin();
                }
              }}
            >
              <MapPinOff className="h-3 w-3" />
              Unpinned only
              <X className="h-3 w-3" />
            </Badge>
          </div>
        )}
        {/* Grid or list — never both. Same filters, same selection handlers,
            same record source, so the switch changes presentation only.
            See [[records-view.spec]] US-004. */}
        {viewMode === "list" ? (
          <PhotoList
            projectId={projectId}
            filters={state.filters}
            metadataFields={project.metadata_fields}
            plans={project.plans}
            onRecordClick={handleRecordClick}
            activeRecordId={isSplit ? (selectedRecord?.id ?? null) : null}
            isSelectionMode={isSelectionMode}
            selectedIds={selectedIds}
            onToggleSelection={handleToggleSelection}
            onLongPressRecord={handleLongPressRecord}
            onItemsChange={handleItemsChange}
            canRaiseIssue={can("issue:Create")}
          />
        ) : (
          <PhotoGrid
            projectId={projectId}
            filters={state.filters}
            onRecordClick={handleRecordClick}
            activeRecordId={isSplit ? (selectedRecord?.id ?? null) : null}
            isSelectionMode={isSelectionMode}
            selectedIds={selectedIds}
            onToggleSelection={handleToggleSelection}
            onLongPressRecord={handleLongPressRecord}
            onItemsChange={handleItemsChange}
          />
        )}
      </div>

      {isSplit && (
        <div
          ref={panelRef}
          style={{ width: sidePanelWidth(availableWidth) }}
          className="flex h-full shrink-0 flex-col border-l border-border"
        >
          <ImageViewerModal {...viewerProps} presentation="panel" />
        </div>
      )}
      </div>

      {!isSplit && (
        <ImageViewerModal
          {...viewerProps}
          presentation={layout === "fullscreen" ? "fullscreen" : "dialog"}
        />
      )}

      <AlertDialog
        open={pendingRecord !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRecord(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              Unsaved changes will be discarded. Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12">Go back</AlertDialogCancel>
            <AlertDialogAction
              className="h-12"
              onClick={() => {
                setSelectedRecord(pendingRecord);
                setPendingRecord(null);
              }}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ReportGenerationDialog
        isOpen={isReportDialogOpen}
        onOpenChange={handleReportDialogOpenChange}
        selectedRecords={selectedRecords}
        projectId={projectId}
        metadataFields={project.metadata_fields ?? []}
        onRemoveRecord={handleRemoveReportRecord}
        reportName={reportName}
        onReportNameChange={setReportName}
        remarks={reportRemarks}
        onRemarksChange={setReportRemarks}
        overrideRemarks={reportOverrideRemarks}
        onOverrideRemarksChange={setReportOverrideRemarks}
        onRequestAddMore={handleRequestAddMore}
      />

      {issueBatch && (
        <RaiseIssueSheet
          projectId={projectId}
          open
          onOpenChange={(open) => {
            if (!open) setIssueBatch(null);
          }}
          records={issueBatch.records}
          skippedCount={issueBatch.skippedCount}
          onBatchRaised={handleBatchRaised}
          onBatchComplete={handleBatchComplete}
        />
      )}
    </div>
  );
}
